import type { HttpClient, Result } from "../client";
import { BaseService } from "../common/base-service";
import { transformKeys } from "../common/utils";
import {
	type CreateThreadInput,
	type GetAllThreadsQuery,
	type Thread,
	ThreadCreateRequestSpeed_selection,
	type ThreadMessage,
	ThreadType,
} from "../types";
import type { ThreadCreateRequest, ThreadLabelRequest } from "../types/threads";

// NOTE: (bwilytsch): This is temporary
// TODO: (bwilytsch): Replace with `codegen` types

interface CreateThreadMessageInput {
	id: string;
	message: string;
	fileUris: string[];
	inputWidgetResponses: unknown[];
}

const createThreadDefaults = (threadId: string): CreateThreadInput => ({
	thread_id: threadId,
	message_id: "",
	prompt: "",
	unified_uris: [],
	input_widget_responses: [],
	thread_type: ThreadType.chat,
	path: null,
	speed_selection: ThreadCreateRequestSpeed_selection.smarter,
});

export class ThreadService extends BaseService {
	constructor(client: HttpClient) {
		super(client, "/threads");
	}

	public async getThreads(
		inputs: GetAllThreadsQuery = {
			after_timestamp: null,
		},
	): Promise<Result<Thread[]>> {
		return this._client.get<Thread[]>(this._prefix, {
			query: inputs,
		});
	}

	public async getThreadById(id: string): Promise<Result<Thread>> {
		return this._client.get<Thread>(`${this._prefix}/:id`, { params: { id } });
	}

	public async deleteThread(id: string): Promise<Result<void>> {
		return this.updatedThreadLabels(id, {
			add_labels: ["TRASH"],
			remove_labels: [],
		});
	}

	public async createThread(
		input: ThreadCreateRequest,
	): Promise<Result<{ id: string }>> {
		const opts = Object.assign(
			createThreadDefaults(input.thread_id ?? ""),
			input,
		);

		return this._client.post<{ id: string }>(this._prefix, {
			data: transformKeys(opts),
		});
	}

	public async changeThreadReadState(
		threadId: string,
		isRead: boolean,
	): Promise<Result<void>> {
		return this.updatedThreadLabels(threadId, {
			add_labels: isRead ? [] : ["UNREAD"],
			remove_labels: isRead ? ["UNREAD"] : [],
		});
	}

	public async getThreadMessages(
		threadId: string,
	): Promise<Result<ThreadMessage[]>> {
		return this._client.get<ThreadMessage[]>(
			`${this._prefix}/:threadId/messages`,
			{
				params: {
					threadId,
				},
			},
		);
	}

	public updatedThreadLabels(
		threadId: string,
		input: ThreadLabelRequest,
	): Promise<Result<void>> {
		return this._client.post(`${this._prefix}/:threadId/change_labels`, {
			params: { threadId },
			data: input,
		});
	}

	public async bulkUpdateThreadLabels(input: {
		thread_ids: string[];
		add_labels: string[];
		remove_labels: string[];
	}): Promise<
		Result<{
			success_count: number;
			failed_count: number;
			errors: string[];
		}>
	> {
		return this._client.post(`${this._prefix}/bulk/change_labels`, {
			data: input,
		});
	}

	public async createThreadMessage(
		threadId: string,
		input: CreateThreadMessageInput,
	): Promise<Result<{ id: string }>> {
		return this._client.post<{ id: string }>(
			`${this._prefix}/:threadId/messages`,
			{
				params: {
					threadId,
				},
				data: input,
			},
		);
	}
}
